import React from 'react'
import { Motion, spring } from 'react-motion'
import filter from 'lodash/filter'
import { StudentList as LiveViewMainStudentList } from '@plickers/now-player-library/chrome'
import { PlayerSlide, responseTotals, screenFlags } from '@plickers/now-player-library/player'
import { revisitConsent } from '../../utils/cookieHelpers'

const springConfig = { stiffness: 170, damping: 26 }

class LiveViewMain extends React.PureComponent {
  render() {
    const {
      currentPollId,
      currentSetPollId,
      currentSetPollPollId,
      layout,
      section,
      showGraph,
      showCorrect,
      questionStage,
      poll,
      isSurvey,
      liveViewSettings,
      snapshot,
      isYoutubeAllowed,
    } = this.props
    const students = filter(section.students, { archived: false })
    const { isAskScreen, isScanScreen } = screenFlags(questionStage)
    // Base values only; filtered to the review and scan screens. A fresh object
    // each call, so it is unpacked here rather than passed down.
    const { processedResponses, totalResponses, expectedResponses } = responseTotals({ stage: questionStage, poll, section })
    let showStudentList
    if (liveViewSettings.studentListVisible === 'always') {
      showStudentList = true
    } else if (liveViewSettings.studentListVisible === 'never') {
      showStudentList = false
    } else if (liveViewSettings.studentListVisible === 'scan') {
      showStudentList = isScanScreen
    }

    const slideScanTransitionStyle = showStudentList ?
      { // when displayed
        transform: `translateY(${layout.yPosScanTranslate}px) translateX(${layout.xPosScanTranslate}px) scale(${layout.scanSlideScale})`,
      } :
      { // when not displayed
        transform: 'translateY(0px) translateX(0px) scale(1)',
      }
    const slideScanStudentListStyle = showStudentList ?
      { // when displayed
        xTranslate: spring(0, springConfig),
        opacity: spring(1, springConfig),
      } :
      { // when not displayed
        xTranslate: spring(layout.studentListxPosScanTranslate, springConfig),
        opacity: spring(0, springConfig),
      }
    let studentLetterBlockSideDimension
    if (layout) {
      studentLetterBlockSideDimension = layout.studentListItemHeight - layout.studentListItemPillMargin - layout.studentListItemPillMargin
    }

    let studentSortOrder = 'displayName'
    if (liveViewSettings.studentSortOrder === 'card') {
      studentSortOrder = 'card'
    }
    const {
      showStudentCardNumbers, alwaysShowLetterblock, colorScheme, showResponseCounts,
    } = liveViewSettings

    const lettersArray = ['A', 'B', 'C', 'D']
    const correctLetters = []

    // Marked against the question on screen (control.snapshot), as the slide
    // is; the poll's copy is the fallback.
    const scoredQuestion = snapshot || (poll && poll.snapshot)
    if (poll && scoredQuestion) {
      scoredQuestion.choices.map((choice, index) => {
        if (choice.correct === true) {
          const correctLetter = lettersArray[index]
          correctLetters.push(correctLetter)
        }
        return null
      })
    }

    let key = ''
    if (currentPollId) {
      key = currentPollId
    } else if (currentSetPollId && currentSetPollPollId) {
      key = `${currentSetPollId}_${currentSetPollPollId}`
    }

    const animationDisabled = liveViewSettings.disableZoomAnimations
    return (
      <React.Fragment>

        {/*
          The slide, its media overlays and the zoom chrome (letter-block panel,
          dark underlay, unzoom button) are the shared library's PlayerSlide. It
          derives the zoom state from control.zoomedItem, gated on page
          visibility as this container used to. `key` remounts it per poll, as
          every overlay and the slide were keyed before.
        */}
        {snapshot && (
        <PlayerSlide
          // Prefixed: <Motion> below is a sibling keyed by the same poll id, and two
          // siblings sharing a key leave the previous slide mounted.
          key={`slide_${key}`}
          className='liveView-slide'
          slideStyle={{
            width: `${layout.slideDimensions.width}px`,
            height: `${layout.slideDimensions.height}px`,
            top: `${layout.yPosSlide}px`,
            left: `${layout.xPosSlide}px`,
            transformOrigin: 'top left',
            zIndex: 2000,
            ...slideScanTransitionStyle,
          }}
          question={snapshot}
          slideWidth={layout.slideDimensions.width}
          slideDimensions={layout}
          layoutKey={this.props.layoutKey}
          showStudentList={showStudentList}
          animationDisabled={animationDisabled}
          visible={this.props.visible}
          pageVisibilityState={this.props.pageVisibilityState}
          zoomedItem={this.props.zoomedItem}
          onZoomMedia={this.props.zoomMedia}
          onToggleZoomMedia={this.props.toggleZoomMedia}
          onUnzoomMedia={this.props.unzoomMedia}
          showMouseMoveControls={this.props.showMouseMoveControls}
          playback={{ item: this.props.playbackItem, isPlaying: this.props.isPlaying }}
          updateControlPlayback={this.props.updateControlPlayback}
          clientPlaybackCommand={this.props.clientPlaybackCommand}
          clearPlaybackCommand={this.props.clearPlaybackCommand}
          showAutoplayFailedAlert={this.props.showAutoplayFailedAlert}
          hideAutoplayFailedAlert={this.props.hideAutoplayFailedAlert}
          youtubeAllowed={isYoutubeAllowed}
          onRevisitConsent={revisitConsent}
          enableYtCookieOverlayActionableButton
          // The slide's own props, as LiveViewMain gave StaticQuestionSlide. `screen`
          // replaces isAskScreen/isScanScreen/isReviewScreen: the library's Slide
          // sets those from it, overriding any passed directly.
          screen={questionStage}
          showResponseCounts={showResponseCounts}
          alwaysShowLetterblock={alwaysShowLetterblock}
          colorScheme={colorScheme}
          isLiveView
          reportView={false}
          isSurvey={isSurvey}
          showGraph={showGraph}
          showCorrect={showCorrect}
          processedResponses={processedResponses}
          totalResponses={totalResponses}
          expectedResponses={expectedResponses}
          xPosSlide={layout.xPosSlide}
          yPosSlide={layout.yPosSlide}
          viewportWidth={layout.viewportWidth}
          viewportHeight={layout.viewportHeight}
        />
        )}

        {/*
          The slide-in stays here: the library's StudentList takes xTranslate
          and opacity as plain numbers rather than depending on react-motion.
          `key` sits on <Motion>, not the list, so a new poll remounts the
          spring exactly as it did when <Motion> lived inside the list.
        */}
        <Motion key={key} style={slideScanStudentListStyle}>
          {({ xTranslate, opacity }) => (
            <LiveViewMainStudentList
              xTranslate={xTranslate}
              opacity={opacity}
              showStudentCardNumbers={showStudentCardNumbers}
              colorScheme={colorScheme}
              layout={layout}
              students={students}
              manualShowIndividualStudentResponses={this.props.manualShowIndividualStudentResponses}
              showIndividualStudentResponses={liveViewSettings.showIndividualResponses}
              isAskScreen={isAskScreen}
              showCorrect={showCorrect}
              poll={poll}
              isSurvey={isSurvey}
              correctLetters={correctLetters}
              isScanScreen={isScanScreen}
              studentSortOrder={studentSortOrder}
              studentLetterBlockSideDimension={studentLetterBlockSideDimension}
              nowPlayingMode={this.props.nowPlayingMode}
              onlineStudents={this.props.onlineStudents}
            />
          )}
        </Motion>
      </React.Fragment>
    )
  }
}

export default LiveViewMain
